import Constants from '@/AppConstants';
import { ISearchBoxViewModelParameters, SearchBoxViewModelSubmitHandler } from '#/DefaultViewExtensionInterfaces';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import * as DateHelper from '@/Utility/DateHelper';
import StorageHelper from '@/Utility/StorageHelper';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import AutoCompleteViewModel from '@/ViewModels/Controls/AutoCompleteViewModel';
import ButtonViewModel from '@/ViewModels/Controls/ButtonViewModel';
import CheckBoxViewModel from '@/ViewModels/Controls/CheckBoxViewModel';
import DatePickerViewModel from '@/ViewModels/Controls/DatePickerViewModel';
import DropDownListViewModel from '@/ViewModels/Controls/DropDownListViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModel from '@/ViewModels/Controls/LinkViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import { IExtensionModuleDTO, IHistorizationPointDTO, ISearchQueryDTO, ITypeMetaDTO } from "coreframetypings/dto";

export default class SearchBoxViewModel extends ControlViewModel {
    private CheckBoxViewModel = ComponentManager.getViewModel<typeof CheckBoxViewModel>('CheckBoxViewModel');
    private DatePickerViewModel = ComponentManager.getViewModel<typeof DatePickerViewModel>('DatePickerViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModel>('DropDownListViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModel>('ImageViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModel>('LinkViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModel>('ButtonViewModel');

    /**
     * Die verfügbaren Erweiterungsmodule
     */
    public extensionModules: KnockoutObservable<IExtensionModuleDTO[]>;

    /**
     * Die verfügbaren Historisierungspunkte
     */
    public historizationPoints: KnockoutObservable<IHistorizationPointDTO[]>;

    /**
     * Ein Handler, der aufgerufen wird, wenn die Eingabe mit Enter bestätigt wird
     */
    public onSubmitHandler: SearchBoxViewModelSubmitHandler;

    public lastSearchValues: KnockoutObservable<unknown[]>;
    public lastSearchValuesCount: number;

    public timezoneLabel: LabelViewModel;
    public timezoneFromLabel: LabelViewModel;
    public timezoneFrom: DatePickerViewModel;
    public timezoneUntilLabel: LabelViewModel;
    public timezoneUntil: DatePickerViewModel;

    public findInHistory: CheckBoxViewModel;

    public deadlineLabel: LabelViewModel;
    public deadline: DropDownListViewModel;

    public modulesLabel: LabelViewModel;
    public modules: KnockoutObservable<CheckBoxViewModel[]>;

    public autoComplete: AutoCompleteViewModel;

    public objectTypeLabel: LabelViewModel;
    public objectType: DropDownListViewModel;

    public searchAreaLabel: LabelViewModel;
    public searchArea: CheckBoxViewModel;

    public checkedModules: KnockoutObservable<IExtensionModuleDTO[]>;
    public checkedModulesWithoutTypes: KnockoutObservable<{
        alias: string,
        ergName: string,
        title: string,
        types: []
    }[]>;

    public selectedType: KnockoutObservable<ITypeMetaDTO>;

    public allowValidation: boolean;

    public isSearchEnabled: KnockoutObservable<boolean>;
    public isCustomParamsSpecified: KnockoutObservable<boolean>;

    public link: LinkViewModel;

    public reset: ButtonViewModel;

    public search: ButtonViewModel;

    public isPopupOpen: KnockoutObservable<boolean>;

    constructor(params: ISearchBoxViewModelParameters) {
        super(params);

        this.allowValidation = true; // wird false, wenn resetted wird, damit dabei kein Fehler auftaucht

        this.extensionModules = this.observableOrDefault(params.extensionModules, []);
        this.historizationPoints = this.observableOrDefault(params.historizationPoints, []);

        this.onSubmitHandler = this.handlerOrDefault(params.onSubmit);

        if (params.lastSearchValues) {
            this.lastSearchValues = this.observableOrDefault(params.lastSearchValues);
        } else {
            this.lastSearchValues = this.defineComputed({
                read: () => {
                    return StorageHelper.get('lastSearchValues') || [];
                },
                write: (value) => {
                    StorageHelper.set('lastSearchValues', value);
                }
            });
        }

        this.lastSearchValuesCount = this.paramOrDefault(params.lastSearchValuesCount, 5);

        this.timezoneLabel = this.paramOrDefault(params.timezoneLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.timeframe.label',
            ergName: 'SEARCH_TIMEFRAME'
        }));

        this.timezoneFromLabel = this.paramOrDefault(params.timezoneFromLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.timeframe.from.label',
            ergName: 'SEARCH_TIMEFRAME_FROM'
        }));
        this.timezoneFrom = this.paramOrDefault(params.timezoneFrom, new this.DatePickerViewModel({
            parentLocator: this.locator,
            locator: 'options.timeframe.from.pick'
        }));

        this.timezoneUntilLabel = this.paramOrDefault(params.timezoneUntilLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.timeframe.until.label',
            ergName: 'SEARCH_TIMEFRAME_TO'
        }));
        this.timezoneUntil = this.paramOrDefault(params.timezoneUntil, new this.DatePickerViewModel({
            parentLocator: this.locator,
            locator: 'options.timeframe.until.pick'
        }));

        this.findInHistory = this.paramOrDefault(params.findInHistory, new this.CheckBoxViewModel({
            parentLocator: this.locator,
            locator: 'options.history',
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'options.history.label',
                ergName: 'SEARCH_HISTORY'
            })
        }));

        this.deadlineLabel = this.paramOrDefault(params.deadlineLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.history.deadline.label',
            ergName: 'SEARCH_HISTORY_DATE'
        }));

        this.deadline = this.paramOrDefault(params.deadline, new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'options.history.deadline.value',
            data: this.defineComputed(() => {
                var historizationPoints = this.historizationPoints();
                var result = [
                    {
                        id: '0',
                        caption: ResourceManager.getInstance().resources()[Constants.SEARCH_HISTORY_DATE_ALL]
                    }
                ];

                for (var i = 0; i < historizationPoints.length; i++) {
                    result.push({
                        id: historizationPoints[i].pdoId,
                        caption: DateHelper.toString(DateHelper.parseDotNetDateString(historizationPoints[i].historizationDate), false, true)
                    });
                }

                return result;
            }),
            value: 0,
            isEnabled: this.defineComputed(() => {
                return this.findInHistory.isChecked();
            }),
            dataTextField: 'caption',
            dataValueField: 'id'
        }));

        this.modules = this.defineComputed(() => {
            const modules = [];

            _.each(this.extensionModules(), (module) => {
                modules.push(new this.CheckBoxViewModel({
                    parentLocator: this.locator,
                    locator: 'options.modules.[' + module.alias + ']',
                    label: new this.LabelViewModel({
                        parentLocator: this.locator,
                        locator: 'options.modules.[' + module.alias + '].label',
                        caption: module.title
                    }),
                    isChecked: true
                }));
            });

            return modules;
        });


        this.checkedModules = this.defineComputed(() => {
            const checkedModules = [];

            _.each(this.modules(), (module) => {
                if (module.isChecked() === true) {
                    _.each(this.extensionModules(), (realModule) => {
                        if (realModule.title === ko.unwrap(module.label.caption)) {
                            checkedModules.push(realModule);
                        }
                    });
                }
            });

            return checkedModules;
        });

        this.autoComplete = this.paramOrDefault(params.autoComplete, null);

        this.modulesLabel = this.paramOrDefault(params.modulesLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.modules.label',
            ergName: 'SEARCH_CONSIDERED_MODULES'
        }));

        this.objectTypeLabel = this.paramOrDefault(params.objectTypeLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.object-types.label',
            ergName: 'SEARCH_OBJECT_TYPE'
        }));
        this.objectType = this.paramOrDefault(params.objectType, new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'options.object-types.value',
            data: this.defineComputed(() => {
                const data = [];

                _.each(this.checkedModules(), (module) => {
                    _.each(module.types, (type) => {
                        const caption = ResourceManager.getInstance().get(type.ergName);

                        const thisType = {
                            value: type.name,
                            name: caption,
                            caption: caption
                        };

                        const inData = _.find(data, d => d.value === thisType.value);

                        if (inData == null) {
                            data.push(thisType);
                        }
                    });
                });

                const counted = _.countBy(data, type => type.name);

                for (const name in counted) {
                    if (counted[name] > 1) {
                        for (let j = 0; j < data.length; j++) {
                            if (data[j].name === name) {
                                data[j].caption = this.getLastTwoSegments(data[j].value, data[j].name);
                            }
                        }
                    }
                }

                const sorted = _.sortBy(data, 'name');
                return sorted;
            }),
            height: 500,
            dataTextField: 'caption',
            dataValueField: 'value',
            optionLabel: ResourceManager.getInstance().get('SEARCH_OBJECT_TYPE_NO_RESTRICTION')
        }));

        this.searchAreaLabel = this.paramOrDefault(params.seekAreaLabel, new this.LabelViewModel({
            parentLocator: this.locator,
            locator: 'options.seek-area.label',
            ergName: 'SEARCH_SEARCH_AREA'
        }));

        this.searchArea = new this.CheckBoxViewModel({
            parentLocator: this.locator,
            locator: 'options.seek-area.Checkbox',
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'options.seek-area.Checkbox.label',
                ergName: 'SEARCH_SEARCH_AREA_TITLE'
            }),
            isChecked: true
        });

        this.checkedModulesWithoutTypes = this.defineComputed(() => {
            const checkedModulesWithoutTypes = [];

            _.each(this.checkedModules(), (module) => {
                checkedModulesWithoutTypes.push({
                    alias: module.alias,
                    ergName: module.ergName,
                    title: module.title,
                    types: []
                });
            });

            return checkedModulesWithoutTypes;
        });

        this.selectedType = this.defineComputed(() => {
            let selectedType: ITypeMetaDTO = null;
            const value = this.objectType.value();

            _.each(this.checkedModules(), (module) => {
                _.each(module.types, (type) => {
                    if (type.name === value) {
                        selectedType = type;
                    }
                });
            });

            return selectedType;
        });

        this.isSearchEnabled = this.defineComputed(() => {
            const fromIsValid = this.timezoneFrom.isValid();
            const untilIsValid = this.timezoneUntil.isValid();
            const checkedSomeModules = this.modules().some(m => m.isChecked());
            return fromIsValid && untilIsValid && checkedSomeModules;
        });

        this.isCustomParamsSpecified = this.defineComputed(() => {
            const unselectedSomeModules = this.modules().some(m => !m.isChecked());
            const timeframeCanReset = this.timezoneFrom.canReset() || this.timezoneUntil.canReset();
            const searchTypeSpecified = this.selectedType() != null;
            const isSearchHistory = this.findInHistory.isChecked();
            const isSearchArea = this.searchArea.isChecked();

            return unselectedSomeModules || timeframeCanReset || searchTypeSpecified || isSearchHistory || !isSearchArea;
        });

        this.link = this.paramOrDefault(params.link, new this.LinkViewModel({
            parentLocator: this.locator,
            locator: 'open-options',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'open-options.image',
                source: this.definePureComputed(() => {
                    var customParamsSpecified = this.isCustomParamsSpecified();
                    return customParamsSpecified ? GlobalConfiguration.inlineImages.CUSTOM_SEARCH_ACTIVATED : GlobalConfiguration.inlineImages.CUSTOM_SEARCH_DEACTIVATED;
                }),
                alternativeText: new this.LabelViewModel({
                    ergName: 'SEARCH_CUSTOM_BUTTON_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'SEARCH_CUSTOM_BUTTON_TOOLTIP'
            })
        }));

        this.reset = this.paramOrDefault(params.reset, new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'options.reset',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'options.reset.image',
                source: GlobalConfiguration.inlineImages.RESET,
                width: 16,
                height: 16,
                alternativeText: new this.LabelViewModel({
                    ergName: 'RESET'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'options.reset.label',
                ergName: 'RESET'
            }),
            isEnabled: this.isCustomParamsSpecified,
            onClick: () => {
                this.allowValidation = false;

                _.each(this.modules(), m => m.isChecked(true));

                this.searchArea.isChecked(true);
                //this.autoComplete.value(''); // Der eingegebene Suchbegriff soll nicht geleert werden
                this.objectType.value(null);

                this.timezoneFrom.resetValue();
                this.timezoneUntil.resetValue();

                this.deadline.value(0);
                this.findInHistory.isChecked(false);

                this.allowValidation = true;
            }
        }));

        this.search = this.paramOrDefault(params.search, new this.ButtonViewModel({
            parentLocator: this.locator,
            locator: 'options.start-search',
            image: new this.ImageViewModel({
                parentLocator: this.locator,
                locator: 'options.start-search.image',
                source: GlobalConfiguration.inlineImages.MAGNIFYING_GLASS,
                alternativeText: new this.LabelViewModel({
                    ergName: 'SEARCH'
                })
            }),
            label: new this.LabelViewModel({
                parentLocator: this.locator,
                locator: 'options.start-search.label',
                ergName: 'SEARCH'
            }),
            onClick: () => {
                this.isPopupOpen(false);
                this.onSubmit(this.autoComplete.value());
            },
            tooltip: new this.LabelViewModel({
                ergName: 'SEARCH_BUTTON_INFO'
            }),
            isEnabled: this.isSearchEnabled
        }));

        this.isPopupOpen = this.observableOrDefault(false).extend({ notify: 'always' });
    }

    private getLastTwoSegments(name: string, caption: string): string {
        const splitted = name.split('.');
        const count = splitted.length;

        if (count <= 2) {
            return name;
        } else {
            return splitted[count - 2] + '.' + caption;
        }
    }

    private getTypeNameByPdoId(pdoId: unknown): string {
        let result: string = null;
        _.each(this.checkedModules(), (module) => {
            const type = _.find(module.types, (type) => {
                return type.pdoId === pdoId;
            });

            if (type)
                result = type.name;
        });

        return result;
    }

    private selectModules(extensionModules: IExtensionModuleDTO[]): void {
        if (!extensionModules)
            return;

        _.each(this.modules(), (module) => {
            const found = _.find(extensionModules, (extModule) => {
                return extModule.title === ko.unwrap(module.label.caption);
            });

            module.isChecked(!!found);
         });
    }

    public setSearchQuery(searchQuery: ISearchQueryDTO): void {
        if (!searchQuery)
            return;

        this.timezoneFrom.value(searchQuery.from);
        this.timezoneUntil.value(searchQuery.to);
        this.findInHistory.isChecked(searchQuery.includeHistory);
        this.deadline.value(searchQuery.historizationPointPDOID);
        this.searchArea.isChecked(searchQuery.searchInAllPrincipals);

        const typeName = searchQuery.selectedType ? this.getTypeNameByPdoId(searchQuery.selectedType.pdoId) : null;
        this.objectType.value(typeName);

        this.selectModules(searchQuery.extensionModules);
    }

    /**
     * Erstellt ein SearchQuery-Objekt mit dem eine Suche durchgeführt werden kann
     */
    public buildSearchQuery(pageSize: number, query?: string): ISearchQueryDTO {
        query = query || this.autoComplete.value();

        const from = this.timezoneFrom.value();
        const to = this.timezoneUntil.value();
        const includeHistory = this.findInHistory.isChecked();
        const historizationPointPDOID = this.deadline.value();
        const extensionModules = this.checkedModulesWithoutTypes();
        const selectedType = this.selectedType();
        const searchArea = this.searchArea.isChecked();
        const sortField = null;
        const reverseSort = false;

        return {
            query: query,
            from: from,
            to: to,
            includeHistory: includeHistory,
            historizationPointPDOID: historizationPointPDOID,
            pageSize: pageSize,
            extensionModules: extensionModules,
            selectedType: selectedType,
            sortField: sortField,
            reverseSort: reverseSort,
            searchInAllPrincipals: searchArea
        };
    }

    private validateTimeFrame(): boolean {
        const from = this.timezoneFrom.date();
        const until = this.timezoneUntil.date();
        return (from == null || until == null || from.getTime() <= until.getTime());
    }

    public validateDatePicker(datePicker: DatePickerViewModel): void {
        if (!datePicker.customValidate(datePicker.rawValue(), true)) {
            // Der Fehler für ein falsches Datum soll Priorität haben
            if (datePicker.invalidationText() == ResourceManager.getInstance().get('SEARCH_DATETIME_VALIDATION')) {
                datePicker.invalidationText('');
            }
            datePicker.isValid(false);
            datePicker.invalidationText(ResourceManager.getInstance().get('PROPERTY_CONSTRAINT'));
            return;
        }
        if (this.allowValidation && !this.validateTimeFrame()) {
            // Wenn nach dem beheben eines Falschen Formats ein Zeitzaum fehler vorliegt muss der invalidation Text eimal zurückgesetzt werden
            if (datePicker.invalidationText() == ResourceManager.getInstance().get('PROPERTY_CONSTRAINT')) {
                datePicker.invalidationText('');
            }
            datePicker.isValid(false);
            datePicker.invalidationText(ResourceManager.getInstance().get('SEARCH_DATETIME_VALIDATION'));
            return;
        }
        
        // Der DatePicker, ist hier auf jeden Fall valid
        datePicker.isValid(true);
        datePicker.invalidationText('');
    }

    public onSubmit(query?: string): void {
        const searchValues = this.lastSearchValues();
        // Wenn schon in den Einträgen vorhanden, wird der Eintrag entfernt
        const index = searchValues.indexOf(query);
        if (index > -1)
            searchValues.splice(index, 1);
        // und vorne in das Array eingefügt
        if(query)
            searchValues.splice(0, 0, query);

        // Zu viele Einträge entfernen
        while (searchValues.length > this.lastSearchValuesCount) {
            searchValues.pop();
        }

        this.lastSearchValues(searchValues);

        // Aufruf des externen Handlers
        this.onSubmitHandler(query);
    }

    public clear(): void {
        this.autoComplete.clear();
    }
}

ComponentManager.setViewModel('SearchBoxViewModel', SearchBoxViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');